import type { PageParams } from '@/domain/entities/paging'

/**
 * Jenis pembayaran berasal dari daftar tetap BE (readme-BE "Metode pembayaran:
 * `name` bebas, `kind` dari daftar tetap"). Aplikasi kasir berperilaku beda per
 * jenis — menampilkan gambar QRIS, membuka layar EDC, atau menghitung kembalian
 * — jadi nilainya tidak boleh diketik bebas.
 */
export const PAYMENT_KINDS = [
  'cash',
  'qris_static',
  'qris_dynamic',
  'card',
  'transfer',
  'ewallet',
  'other',
] as const

export type PaymentKind = (typeof PAYMENT_KINDS)[number]

/** Metode pembayaran milik SATU outlet, bukan tenant. */
export interface PaymentMethod {
  readonly id: string
  readonly name: string
  readonly kind: PaymentKind
  /** Wajib untuk `qris_static`; tanpa gambar tombolnya kosong di layar kasir. */
  readonly imageUrl: string | null
  /** URL QRIS Statis terkait, digunakan untuk metode `qris_dynamic`. */
  readonly urlQrisStatis: string | null
  /** Kolom sendiri, bukan turunan `kind === 'cash'` — ada outlet yang memberi
   *  kembalian dari voucher dan ada yang menolaknya untuk transfer berlebih. */
  readonly allowChange: boolean
  /** Kasir wajib mengisi nomor referensi (EDC/transfer). */
  readonly requireReference: boolean
  readonly accountName: string | null
  readonly accountNumber: string | null
  /** Nama integrator, disiapkan untuk QRIS dinamis. */
  readonly provider: string | null
  /** Urutan tombol di layar kasir; seri dipecah oleh nama. */
  readonly sortOrder: number
  readonly active: boolean
}

/**
 * Payload buat/ubah. `active` tidak ada di sini: baris baru selalu aktif, dan
 * menonaktifkan/menghidupkan punya jalurnya sendiri (DELETE & restore).
 */
export interface PaymentMethodInput {
  readonly name: string
  readonly kind: PaymentKind
  readonly imageUrl: string | null
  readonly urlQrisStatis: string | null
  readonly allowChange: boolean
  readonly requireReference: boolean
  readonly accountName: string | null
  readonly accountNumber: string | null
  readonly provider: string | null
  readonly sortOrder: number
}

/**
 * `active` adalah dimensi filter di luar paging: null = semua (dashboard admin,
 * supaya yang nonaktif bisa dihidupkan lagi), true/false = disaring.
 */
export type PaymentMethodListParams = PageParams & { readonly active: boolean | null }

/**
 * BE menolak `qris_static` tanpa `image_url` dengan 400 bermesan generik
 * ("input tidak valid"), jadi aturannya dicek juga di sini supaya admin dapat
 * pesan yang menyebut field-nya.
 */
export function missingQrisImage(input: PaymentMethodInput): boolean {
  return input.kind === 'qris_static' && !input.imageUrl
}
